import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import CheckboxTree from "react-checkbox-tree";
import { toast } from "react-toastify";
import { treeIcons } from "../../pages/user-management/role-info/RoleInfoModify";
import { ModalTransition } from "../shared/Dialog/Config";

const isRouteLeafNode = (node) =>
  Boolean(
    node &&
      (node.level === 3 ||
        (node.level === 2 && node.link) ||
        (node.link && (!node.child || node.child.length === 0)))
  );

const joinRouteSegments = (...segments) => {
  const parts = segments
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .map((part) => part.replace(/^\/+|\/+$/g, ""));
  if (parts.length === 0) return "/";
  return `/${parts.join("/")}`;
};

const findTreeNodeByValue = (nodes, value) => {
  if (!Array.isArray(nodes) || value == null) return null;
  const wanted = String(value);
  for (const node of nodes) {
    if (String(node.value) === wanted) return node;
    const child = findTreeNodeByValue(node.children, wanted);
    if (child) return child;
  }
  return null;
};

const findTreeNodeByRoutePath = (nodes, routePath) => {
  if (!Array.isArray(nodes) || !routePath) return null;
  const wanted = String(routePath).replace(/\/+$/, "") || "/";
  for (const node of nodes) {
    const nodePath = String(node.routePath || "").replace(/\/+$/, "") || "/";
    if (node.routePath && nodePath === wanted) return node;
    const child = findTreeNodeByRoutePath(node.children, routePath);
    if (child) return child;
  }
  return null;
};

const onMakeRouteNodes = (nodes, parentLink = "") => {
  if (!Array.isArray(nodes)) return { nodes: [], checked: [], expanded: [] };

  let expanded = [];
  const treeNodes = nodes.map((node) => {
    const link = joinRouteSegments(parentLink, node.link);
    const isLeaf = isRouteLeafNode(node);

    const newNode = {
      value: `route:${node.id}`,
      routePath: isLeaf ? link : null,
      label: node.title,
      showCheckbox: isLeaf,
    };

    if (!isLeaf && Array.isArray(node.child) && node.child.length > 0) {
      const childResult = onMakeRouteNodes(node.child, link);
      newNode.children = childResult.nodes;
      expanded = [...expanded, ...childResult.expanded];
      if (childResult.expanded.length > 0) {
        expanded.push(newNode.value);
      }
    }

    return newNode;
  });

  return { nodes: treeNodes, checked: [], expanded };
};

const SelectDefaultUrlDialog = ({
  routes,
  open,
  onClose,
  onOk,
  currentDefaultUrl = "",
}) => {
  const treeModel = useMemo(
    () =>
      onMakeRouteNodes(
        [
          {
            id: "main",
            title: "داشبورد اصلی",
            link: "",
            level: 0,
            child: Array.isArray(routes) ? routes : [],
          },
        ],
        ""
      ),
    [routes]
  );

  const [checked, setChecked] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState("");

  useEffect(() => {
    if (!open) return;

    const initialNode = currentDefaultUrl
      ? findTreeNodeByRoutePath(treeModel.nodes, currentDefaultUrl)
      : null;

    setSelectedRoute(
      initialNode?.routePath ||
        (currentDefaultUrl && currentDefaultUrl !== "/"
          ? currentDefaultUrl
          : "")
    );
    setChecked(initialNode ? [initialNode.value] : []);
    setExpanded(
      Array.from(
        new Set([
          ...(treeModel.expanded || []),
          ...(initialNode ? [initialNode.value] : []),
        ])
      )
    );
  }, [open, currentDefaultUrl, treeModel]);

  const onSelectDefaultUrl = () => {
    if (!selectedRoute) {
      toast.error("لطفا یکی از صفحات را انتخاب کنید!");
      return;
    }
    onOk(selectedRoute);
  };

  const onCheckTreeItem = (nextChecked, clickedNode) => {
    // react-checkbox-tree may not keep custom props (routePath) on the callback node.
    const nodeValue =
      clickedNode?.value ||
      (Array.isArray(nextChecked) && nextChecked.length
        ? nextChecked[nextChecked.length - 1]
        : null);

    const treeNode = findTreeNodeByValue(treeModel.nodes, nodeValue);
    const routePath =
      treeNode?.routePath ||
      clickedNode?.routePath ||
      null;

    if (!routePath) {
      toast.error("برای این آیتم مسیر معتبری تعریف نشده است.");
      return;
    }

    setSelectedRoute(routePath);
    setChecked(nodeValue ? [nodeValue] : []);
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={ModalTransition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>انتخاب مسیر پیش‌فرض</DialogTitle>

      <DialogContent>
        <Grid container spacing={1}>
          {selectedRoute ? (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                مسیر انتخاب‌شده: <strong>{selectedRoute}</strong>
              </Alert>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                یک صفحه برگ را از درخت زیر انتخاب کنید.
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <CheckboxTree
              nodes={treeModel.nodes}
              checked={checked}
              expanded={expanded}
              onCheck={onCheckTreeItem}
              onExpand={setExpanded}
              icons={treeIcons}
              showExpandAll
              noCascade
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className="buttons-wrapper">
        <Button
          variant="outlined"
          color="inherit"
          className="cancel-button"
          onClick={onClose}
        >
          انصراف
        </Button>

        <Button
          className="submit-button"
          onClick={onSelectDefaultUrl}
          variant="contained"
          disabled={!selectedRoute}
        >
          انتخاب
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectDefaultUrlDialog;
